/**
 * The 15 counties and their prefectures — a leaf of `lexicon.ts`, because the browser needs this
 * table as a value and the place lexicon beside it came too. Re-exported there, so no import moved.
 */

import type { Prefecture } from './classify.js';

/**
 * All 15 counties, in the `-maa` nominative the reports use as a heading — and the canonical
 * spelling everything downstream groups by (design §3.5).
 *
 * Alphabetical in Estonian collation, and exported as the closed list so `normaliseCounty`'s return
 * type is the fifteen names rather than `string`: a projection that writes a county the API cannot
 * filter on is then a compile error rather than a facet with sixteen buckets.
 */
export const COUNTIES = [
  'Harjumaa',
  'Hiiumaa',
  'Ida-Virumaa',
  'Jõgevamaa',
  'Järvamaa',
  'Läänemaa',
  'Lääne-Virumaa',
  'Põlvamaa',
  'Pärnumaa',
  'Raplamaa',
  'Saaremaa',
  'Tartumaa',
  'Valgamaa',
  'Viljandimaa',
  'Võrumaa',
] as const;

/** One of the fifteen, canonically spelled. */
export type County = (typeof COUNTIES)[number];

/**
 * Estonia's 15 counties mapped onto the four PPA prefectures — a static table, because the PPA's
 * structure changes on the order of decades. Here, beside `COUNTIES`, so both sides can read it.
 */
export const COUNTY_PREFECTURE: ReadonlyMap<County, Prefecture> = new Map([
  ['Harjumaa', 'NORTH'],
  ['Ida-Virumaa', 'EAST'],
  ['Lääne-Virumaa', 'EAST'],
  ['Jõgevamaa', 'SOUTH'],
  ['Tartumaa', 'SOUTH'],
  ['Valgamaa', 'SOUTH'],
  ['Võrumaa', 'SOUTH'],
  ['Põlvamaa', 'SOUTH'],
  ['Viljandimaa', 'SOUTH'],
  ['Hiiumaa', 'WEST'],
  ['Läänemaa', 'WEST'],
  ['Pärnumaa', 'WEST'],
  ['Raplamaa', 'WEST'],
  ['Saaremaa', 'WEST'],
  ['Järvamaa', 'WEST'],
] satisfies Array<[County, Prefecture]>);

/** `COUNTIES` order, with a name the list does not carry last: the group order every municipality
 * list is sorted into, server and browser alike. */
export function countyRank(county: string): number {
  const index = COUNTIES.indexOf(county as County);
  return index === -1 ? COUNTIES.length : index;
}

/** The counties one prefecture covers, in `COUNTIES` order so the list is stable. */
export function countiesInPrefecture(prefecture: Prefecture): readonly County[] {
  return COUNTIES.filter((county) => COUNTY_PREFECTURE.get(county) === prefecture);
}
